import { useState, useRef, useEffect } from 'react';
import { Send, BookOpen, Sparkles, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';

type Aba = 'normas' | 'llm';
type Msg = { role: 'user' | 'model'; text: string };

export function IAPage() {
  const [aba, setAba] = useState<Aba>('normas');
  const [historicoNormas, setHistoricoNormas] = useState<Msg[]>([]);
  const [historicoLLM, setHistoricoLLM] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const historico = aba === 'normas' ? historicoNormas : historicoLLM;
  const setHistorico = aba === 'normas' ? setHistoricoNormas : setHistoricoLLM;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [historicoNormas, historicoLLM, aba, loading]);

  const enviar = async () => {
    const pergunta = input.trim();
    if (!pergunta || loading) return;
    setErro(null);
    setInput('');

    const novoHist = [...historico, { role: 'user' as const, text: pergunta }];
    setHistorico(novoHist);
    setLoading(true);

    try {
      const resp = aba === 'normas'
        ? await apiService.aiChatNormas(pergunta, historico)
        : await apiService.aiChat(pergunta, historico);

      if (resp.success && resp.data?.resposta) {
        setHistorico([...novoHist, { role: 'model', text: resp.data.resposta }]);
      } else {
        setErro(resp.message || 'Erro ao consultar IA');
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao consultar IA');
    } finally {
      setLoading(false);
    }
  };

  const limpar = () => {
    setHistorico([]);
    setErro(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      enviar();
    }
  };

  const placeholder = aba === 'normas'
    ? 'Pergunte sobre NR-12, ISO 12100, ISO 13849-1, ABNT NBR 14153...'
    : 'Converse com a IA...';

  return (
    <div className="p-6 w-full h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-schmersal-blue dark:text-white">Assistente IA</h1>
        <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          Consulte normas de segurança ou converse com o assistente de IA.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
        <button
          onClick={() => setAba('normas')}
          className={`flex items-center justify-center gap-2 px-6 py-2 min-w-[140px] text-sm font-semibold border-b-2 transition-colors ${
            aba === 'normas'
              ? 'border-schmersal-blue text-schmersal-blue dark:text-white dark:border-white'
              : 'border-transparent text-schmersal-gray-medium hover:text-schmersal-blue'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Normas
        </button>
        <button
          onClick={() => setAba('llm')}
          className={`flex items-center justify-center gap-2 px-6 py-2 min-w-[140px] text-sm font-semibold border-b-2 transition-colors ${
            aba === 'llm'
              ? 'border-schmersal-blue text-schmersal-blue dark:text-white dark:border-white'
              : 'border-transparent text-schmersal-gray-medium hover:text-schmersal-blue'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          LLM
        </button>
        <div className="ml-auto flex items-center">
          <button
            onClick={limpar}
            disabled={historico.length === 0}
            className="text-xs text-schmersal-gray-medium hover:text-schmersal-blue disabled:opacity-40"
          >
            Limpar conversa
          </button>
        </div>
      </div>

      {/* Histórico */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-white dark:bg-schmersal-gray-darker rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4 mb-3 h-[calc(100vh-280px)]"
      >
        {historico.length === 0 && !loading && (
          <div className="h-full flex items-center justify-center text-center text-schmersal-gray-medium dark:text-schmersal-gray-light text-sm">
            {aba === 'normas'
              ? 'Pergunte sobre normas de segurança de máquinas (NR-12, ISO 12100, ISO 13849-1, etc).'
              : 'Faça uma pergunta livre ao assistente de IA.'}
          </div>
        )}

        {historico.map((m, i) => (
          <div
            key={i}
            className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[92%] px-3 py-2 rounded-lg whitespace-pre-wrap text-sm ${
                m.role === 'user'
                  ? 'bg-schmersal-blue text-white'
                  : 'bg-schmersal-gray-light dark:bg-schmersal-gray-dark text-schmersal-gray-darkest dark:text-white'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-schmersal-gray-medium text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Consultando IA...
          </div>
        )}
      </div>

      {erro && (
        <div className="mb-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded">
          {erro}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 items-end">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={loading}
          className="flex-1 px-3 py-2 rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker text-schmersal-gray-darkest dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
        />
        <button
          onClick={enviar}
          disabled={loading || !input.trim()}
          className="px-4 py-3 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          Enviar
        </button>
      </div>
    </div>
  );
}
